import { Component } from 'react';
import { useNavigate } from 'react-router';
import { Home, ArrowLeft, RefreshCw, AlertTriangle, Search, ServerCrash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const EVA_LOGO = "/eva-icon-square.png";
const VISTRY_LOGO = "/vistry-icon.png";

// 404 Not Found Page
export function NotFoundPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Gradient Background */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-emerald-900" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src={EVA_LOGO} alt="Eva" className="w-12 h-12 rounded-xl shadow-lg" />
            <span className="text-2xl font-bold text-white">Eva Insights</span>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl">
                <Search className="w-12 h-12 text-white/80" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
              Page not found
            </h1>
            <p className="text-lg text-white/80 max-w-md">
              The page you're looking for doesn't exist or has been moved. 
              Let's get you back on track.
            </p>
          </div>
          
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <img src={VISTRY_LOGO} alt="Vistry" className="w-5 h-5 rounded" />
            Powered by Vistry
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src={EVA_LOGO} alt="Eva" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold">Eva Insights</span>
          </div>

          <Card>
            <CardContent className="pt-8 pb-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-6">
                <span className="text-5xl font-bold text-emerald-600">404</span>
              </div>
              
              <h2 className="text-2xl font-bold mb-2">Oops! Page not found</h2>
              <p className="text-muted-foreground mb-8">
                We couldn't find what you were looking for. The page may have been moved or deleted.
              </p>

              <div className="space-y-3">
                <Button onClick={() => navigate('/')} className="w-full" size="lg">
                  <Home className="w-5 h-5 mr-2" />
                  Go to Dashboard
                </Button>
                
                <Button onClick={() => navigate(-1)} variant="outline" className="w-full" size="lg">
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Go Back
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Need help?{' '}
            <a href="mailto:support@vistry.ai" className="text-emerald-600 hover:text-emerald-700 font-medium">
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

// 500 Server Error Page
export function ServerErrorPage({ error, resetError }) {
  const navigate = useNavigate();

  const handleRetry = () => {
    if (resetError) resetError();
    window.location.reload();
  };

  const handleGoHome = () => {
    if (resetError) resetError();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-orange-600 to-amber-500" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src={EVA_LOGO} alt="Eva" className="w-12 h-12 rounded-xl shadow-lg" />
            <span className="text-2xl font-bold text-white">Eva Insights</span>
          </div>
          
          <div className="space-y-6">
            <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl inline-block">
              <ServerCrash className="w-12 h-12 text-white/80" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
              Something went wrong
            </h1>
            <p className="text-lg text-white/80 max-w-md">
              We're experiencing some technical difficulties. 
              Our team has been notified and is working on it.
            </p>
          </div>
          
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <img src={VISTRY_LOGO} alt="Vistry" className="w-5 h-5 rounded" />
            Powered by Vistry
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src={EVA_LOGO} alt="Eva" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-bold">Eva Insights</span>
          </div>

          <Card>
            <CardContent className="pt-8 pb-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 mb-6">
                <AlertTriangle className="w-10 h-10 text-red-500" />
              </div>
              
              <h2 className="text-2xl font-bold mb-2">Server Error</h2>
              <p className="text-muted-foreground mb-8">
                Something unexpected happened. Please try again or contact support if the problem persists.
              </p>

              <div className="space-y-3">
                <Button onClick={handleRetry} className="w-full" size="lg">
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Try Again
                </Button>
                
                <Button onClick={handleGoHome} variant="outline" className="w-full" size="lg">
                  <Home className="w-5 h-5 mr-2" />
                  Go to Dashboard
                </Button>
              </div>

              {error && process.env.NODE_ENV === 'development' && (
                <details className="mt-6 text-left">
                  <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                    View error details
                  </summary>
                  <pre className="mt-2 p-3 bg-muted rounded-lg text-xs text-red-600 dark:text-red-400 overflow-auto max-h-32">
                    {error.toString()}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Need help?{' '}
            <a href="mailto:support@vistry.ai" className="text-emerald-600 hover:text-emerald-700 font-medium">
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

// Error Boundary Class Component
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    localStorage.removeItem('eva_token');
    localStorage.removeItem('eva_user');
    localStorage.removeItem('eva_tenant');
    this.setState({ hasError: false, error: null });
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      return (
        <ServerErrorPageStandalone 
          error={this.state.error} 
          resetError={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}

// Standalone version for Error Boundary (no router context)
function ServerErrorPageStandalone({ error, resetError }) {
  const handleRetry = () => {
    if (resetError) resetError();
    window.location.reload();
  };

  const handleGoHome = () => {
    if (resetError) resetError();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50 dark:bg-slate-900">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={EVA_LOGO} alt="Eva" className="w-10 h-10 rounded-xl" />
          <span className="text-xl font-bold text-slate-900 dark:text-white">Eva Insights</span>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 mb-6">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Something went wrong
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-8">
            Eva encountered an unexpected error. Please try again or sign in again.
          </p>

          <div className="space-y-3">
            <button
              onClick={handleRetry}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </button>
            
            <button
              onClick={handleGoHome}
              className="w-full py-3 px-4 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <Home className="w-5 h-5" />
              Go to Dashboard
            </button>
          </div>

          {error && process.env.NODE_ENV === 'development' && (
            <details className="mt-6 text-left">
              <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300">
                View error details
              </summary>
              <pre className="mt-2 p-3 bg-slate-100 dark:bg-slate-900 rounded-lg text-xs text-red-600 dark:text-red-400 overflow-auto max-h-32">
                {error.toString()}
              </pre>
            </details>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          Need help?{' '}
          <a href="mailto:support@vistry.ai" className="text-emerald-600 hover:text-emerald-700 font-medium">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}

export default NotFoundPage;
